import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Loader2,
  RefreshCw,
  Download,
  Trash2,
  LogOut,
  MessageCircle,
} from "lucide-react";
import { useLang } from "@/i18n";
import { BrandMark } from "@/components/site/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "md_admin_token";

const STATUS_STYLE = {
  new: "bg-terracotta/10 text-terracotta border-terracotta/40",
  in_progress: "bg-amber-500/10 text-amber-700 border-amber-500/40",
  quoted: "bg-emerald-500/10 text-emerald-700 border-emerald-500/40",
  closed: "bg-stone-500/10 text-stone-500 border-stone-400/40",
};

const waNumber = (phone) => {
  const digits = (phone || "").replace(/\D/g, "");
  return digits.startsWith("34") ? digits : `34${digits}`;
};

const Admin = () => {
  const { t } = useLang();
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loginError, setLoginError] = useState("");
  const [loggingIn, setLoggingIn] = useState(false);
  const [quotes, setQuotes] = useState(null);

  const authHeader = { headers: { Authorization: `Bearer ${token}` } };

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/quotes`, authHeader);
      setQuotes(data);
    } catch (e) {
      if (e.response?.status === 401) {
        localStorage.removeItem(TOKEN_KEY);
        setToken("");
      } else {
        toast.error(t.admin.loadError);
        setQuotes([]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    if (token) load();
  }, [token, load]);

  const login = async (e) => {
    e.preventDefault();
    setLoggingIn(true);
    setLoginError("");
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem(TOKEN_KEY, data.access_token);
      setToken(data.access_token);
    } catch (err) {
      setLoginError(t.admin.loginError);
    } finally {
      setLoggingIn(false);
    }
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setQuotes(null);
  };

  const setStatus = async (id, status) => {
    try {
      const { data } = await axios.patch(`${API}/quotes/${id}?status=${status}`, null, authHeader);
      setQuotes((qs) => qs.map((q) => (q.id === id ? data : q)));
    } catch {
      toast.error(t.admin.loadError);
    }
  };

  const remove = async (id) => {
    if (!window.confirm(t.admin.deleteConfirm)) return;
    try {
      await axios.delete(`${API}/quotes/${id}`, authHeader);
      setQuotes((qs) => qs.filter((q) => q.id !== id));
    } catch {
      toast.error(t.admin.loadError);
    }
  };

  const exportCsv = () => {
    const head = ["date", "name", "phone", "email", "service", "scale", "message", "status"];
    const rows = (quotes || []).map((q) =>
      [q.created_at, q.name, q.phone, q.email, q.service, q.scale, q.message, q.status]
        .map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`)
        .join(",")
    );
    const blob = new Blob([[head.join(","), ...rows].join("\n")], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "martinezd-solicitudes.csv";
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const serviceLabel = (id) =>
    t.services.items.find((s) => s.id === id)?.title || t.quote.serviceOther;

  if (!token) {
    return (
      <div className="bg-grid-light flex min-h-screen items-center justify-center bg-bone px-5" data-testid="admin-login-page">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
          className="w-full max-w-sm border border-black/10 bg-white p-8"
        >
          <BrandMark size={40} />
          <h1 className="mt-6 font-display text-2xl font-medium text-charcoal" data-testid="admin-login-title">
            {t.admin.loginTitle}
          </h1>
          <p className="mt-1 text-xs text-stone-500">{t.admin.loginSub}</p>
          <form onSubmit={login} className="mt-8 space-y-5" data-testid="admin-login-form">
            <div>
              <label htmlFor="a-email" className="mb-1.5 block font-mono text-[10px] uppercase tracking-[0.2em] text-stone-500">
                {t.admin.email}
              </label>
              <input
                id="a-email"
                data-testid="admin-email-input"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full border border-black/15 px-4 py-3 text-sm focus:border-terracotta focus:outline-none"
              />
            </div>
            <div>
              <label htmlFor="a-pass" className="mb-1.5 block font-mono text-[10px] uppercase tracking-[0.2em] text-stone-500">
                {t.admin.password}
              </label>
              <input
                id="a-pass"
                data-testid="admin-password-input"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full border border-black/15 px-4 py-3 text-sm focus:border-terracotta focus:outline-none"
              />
            </div>
            {loginError && (
              <p data-testid="admin-login-error" className="border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
                {loginError}
              </p>
            )}
            <button
              data-testid="admin-login-btn"
              type="submit"
              disabled={loggingIn}
              className="flex w-full items-center justify-center gap-2 bg-terracotta py-3.5 font-mono text-xs uppercase tracking-[0.2em] text-bone transition-colors hover:bg-terra-dark disabled:opacity-60"
            >
              {loggingIn && <Loader2 size={14} className="animate-spin" />}
              {loggingIn ? t.admin.loggingIn : t.admin.login}
            </button>
          </form>
          <Link to="/" data-testid="admin-back-link" className="mt-6 block font-mono text-[10px] uppercase tracking-[0.2em] text-stone-400 hover:text-terracotta">
            {t.admin.back}
          </Link>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bone" data-testid="admin-dashboard">
      <header className="border-b border-black/10 bg-white">
        <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-4 px-5 py-5 lg:px-10">
          <div>
            <h1 className="font-display text-2xl font-medium text-charcoal" data-testid="admin-title">
              {t.admin.title}
            </h1>
            <p className="text-xs text-stone-500">{t.admin.subtitle}</p>
          </div>
          <div className="flex items-center gap-2">
            <a
              data-testid="admin-brand-kit-btn"
              href="/brand/obras-martinez-brand-pack.zip"
              download
              className="flex items-center gap-2 border border-black/15 px-4 py-2.5 font-mono text-[11px] uppercase tracking-[0.15em] text-stone-600 transition-colors hover:border-terracotta hover:text-terracotta"
            >
              <Download size={13} /> {t.admin.brandKit}
            </a>
            <button
              data-testid="admin-refresh-btn"
              onClick={load}
              className="flex items-center gap-2 border border-black/15 px-4 py-2.5 font-mono text-[11px] uppercase tracking-[0.15em] text-stone-600 transition-colors hover:border-terracotta hover:text-terracotta"
            >
              <RefreshCw size={13} /> {t.admin.refresh}
            </button>
            <button
              data-testid="admin-export-csv-btn"
              onClick={exportCsv}
              disabled={!quotes?.length}
              className="flex items-center gap-2 border border-black/15 px-4 py-2.5 font-mono text-[11px] uppercase tracking-[0.15em] text-stone-600 transition-colors hover:border-terracotta hover:text-terracotta disabled:opacity-40"
            >
              <Download size={13} /> {t.admin.exportCsv}
            </button>
            <button
              data-testid="admin-logout-btn"
              onClick={logout}
              className="flex items-center gap-2 bg-charcoal px-4 py-2.5 font-mono text-[11px] uppercase tracking-[0.15em] text-bone transition-colors hover:bg-terracotta"
            >
              <LogOut size={13} /> {t.admin.logout}
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-5 py-10 lg:px-10">
        {!quotes ? (
          <div className="flex justify-center py-24" data-testid="admin-loading">
            <Loader2 size={28} className="animate-spin text-terracotta" />
          </div>
        ) : quotes.length === 0 ? (
          <p data-testid="admin-empty" className="border border-dashed border-black/15 py-24 text-center font-mono text-xs uppercase tracking-[0.2em] text-stone-400">
            {t.admin.empty}
          </p>
        ) : (
          <div className="overflow-x-auto border border-black/10 bg-white" data-testid="admin-quotes-table">
            <table className="w-full min-w-[900px] text-left text-sm">
              <thead>
                <tr className="border-b border-black/10 font-mono text-[10px] uppercase tracking-[0.18em] text-stone-500">
                  <th className="px-5 py-4">{t.admin.th.date}</th>
                  <th className="px-5 py-4">{t.admin.th.name}</th>
                  <th className="px-5 py-4">{t.admin.th.contact}</th>
                  <th className="px-5 py-4">{t.admin.th.service}</th>
                  <th className="px-5 py-4">{t.admin.th.message}</th>
                  <th className="px-5 py-4">{t.admin.th.status}</th>
                  <th className="px-5 py-4">{t.admin.th.actions}</th>
                </tr>
              </thead>
              <tbody>
                {quotes.map((q) => (
                  <tr key={q.id} data-testid={`quote-row-${q.id}`} className="border-b border-black/5 align-top transition-colors hover:bg-travertine/60">
                    <td className="whitespace-nowrap px-5 py-4 font-mono text-xs text-stone-500">
                      {new Date(q.created_at).toLocaleDateString("es-ES", { day: "2-digit", month: "short" })}
                      <span className="block text-[10px] text-stone-400">
                        {new Date(q.created_at).toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" })}
                      </span>
                    </td>
                    <td className="px-5 py-4 font-medium text-charcoal">{q.name}</td>
                    <td className="px-5 py-4">
                      <a href={`tel:${q.phone}`} className="block font-mono text-xs text-charcoal hover:text-terracotta">
                        {q.phone}
                      </a>
                      {q.email && (
                        <a href={`mailto:${q.email}`} className="mt-1 block font-mono text-[11px] text-stone-400 hover:text-terracotta">
                          {q.email}
                        </a>
                      )}
                    </td>
                    <td className="px-5 py-4 text-xs">
                      {serviceLabel(q.service)}
                      {q.scale && (
                        <span className="mt-1 block font-mono text-[10px] uppercase tracking-widest text-stone-400">
                          {t.admin.scaleLabels[q.scale] || q.scale}
                        </span>
                      )}
                    </td>
                    <td className="max-w-[220px] px-5 py-4 text-xs text-stone-500">{q.message || "—"}</td>
                    <td className="px-5 py-4">
                      <select
                        data-testid={`status-select-${q.id}`}
                        value={q.status}
                        onChange={(e) => setStatus(q.id, e.target.value)}
                        className={`cursor-pointer border px-2.5 py-1.5 font-mono text-[10px] uppercase tracking-widest ${STATUS_STYLE[q.status] || STATUS_STYLE.new}`}
                      >
                        {Object.entries(t.admin.status).map(([id, label]) => (
                          <option key={id} value={id}>
                            {label}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-2">
                        <a
                          data-testid={`quote-whatsapp-${q.id}`}
                          href={`https://wa.me/${waNumber(q.phone)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label="WhatsApp"
                          className="flex h-8 w-8 items-center justify-center border border-black/10 text-stone-500 transition-colors hover:border-[#25D366] hover:text-[#25D366]"
                        >
                          <MessageCircle size={14} />
                        </a>
                        <button
                          data-testid={`quote-delete-${q.id}`}
                          onClick={() => remove(q.id)}
                          aria-label={t.admin.delete}
                          className="flex h-8 w-8 items-center justify-center border border-black/10 text-stone-500 transition-colors hover:border-red-400 hover:text-red-500"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <Link to="/" data-testid="admin-back-link-2" className="mt-8 inline-block font-mono text-[10px] uppercase tracking-[0.2em] text-stone-400 hover:text-terracotta">
          {t.admin.back}
        </Link>
      </main>
    </div>
  );
};

export default Admin;
